import { useMemo } from "react";
import { Table } from "../../components/Table";
import {
  Order,
  getOrderStatusDisplayName,
  canAddChangeOrder,
  canDeleteOrder,
  sortOrderValueForStatusOf,
} from "../../types/order";
import { GridColDef } from "@mui/x-data-grid";
import { Container, Stack, Typography } from "@mui/material";
import { OrderPhaseChip } from "./OrderPhaseChip";
import { formatAsCurrency } from "../../utils/number";
import { formatAsDate } from "../../utils/date";
import {
  MoreMenuButton,
  MoreMenuButtonProps,
} from "../../components/MoreMenuButton";
import { FillContainer } from "../../components/FillContainer";
import { useSession } from "../../hooks/useSession";

type OrderListViewProps = {
  orders: Order[];
  handleOrderEditClick: (orderGuid: string) => void;
  handleNewChangeOrderClick: (orderGuid: string) => void;
  handleDeleteOrderClick: (orderGuid: string) => void;
  handleUnconfirmClick: (orderGuid: string) => void;
};

export const OrderListView: React.FC<OrderListViewProps> = ({
  orders,
  handleOrderEditClick,
  handleNewChangeOrderClick,
  handleDeleteOrderClick,
  handleUnconfirmClick,
}) => {
  const { user } = useSession();

  const columns: GridColDef<Order>[] = useMemo(
    () => [
      {
        field: "orderStatus",
        flex: 1,
        minWidth: 290,
        maxWidth: 390,
        headerName: "Status",
        renderCell: (params) => (
          <Stack
            direction="row"
            justifyItems="center"
            spacing={1}
            sx={{ mt: 2 }}
          >
            <Typography>
              {getOrderStatusDisplayName(params.value.orderStatus)}
            </Typography>
            {params.row.orderStatus === "q" ? (
              <OrderPhaseChip quotePhase={params.row.quotePhase} />
            ) : null}
          </Stack>
        ),
        valueGetter: (_, row) => row,
        sortComparator: (a, b) => {
          const primarySortA = sortOrderValueForStatusOf(a); // Returns 0 to 11
          const primarySortB = sortOrderValueForStatusOf(b);

          if (primarySortA !== primarySortB) {
            return primarySortA - primarySortB; // Ascending order based on primary value
          }

          return a.createdTimestamp - b.createdTimestamp;
        },
      },
      {
        field: "orderIdentifier",
        flex: 1,
        minWidth: 150,
        maxWidth: 200,
        headerName: "ID",
        valueFormatter: (value) => `ID ${value}`,
      },
      {
        field: "calculatedVinNumber",
        flex: 1,
        minWidth: 150,
        maxWidth: 200,
        headerName: "VIN",
        valueFormatter: (value) => value ?? "",
      },
      {
        field: "basePlusOptions",
        headerName: "Base + Options",
        width: 150,
        valueFormatter: (value, order) =>
          order.orderStatus !== "a" ? formatAsCurrency(value) : "",
      },
      {
        field: "createdTimestamp",
        width: 120,
        headerName: "Date Created",
        valueFormatter: (value) => formatAsDate(value),
      },
      {
        field: "modelNumber",
        flex: 1,
        headerName: "Model",
        minWidth: 150,
        maxWidth: 300,
        valueGetter: (_, order) => order.modelNumber ?? "",
      },
      {
        field: "retTenantName",
        flex: 1,
        headerName: "Retailer",
        minWidth: 200,
        valueGetter: (_, order) => order.retTenantName || order.retailerFriendlyName,
        sortComparator: (a, b) => {
          if (!a) return 1;
          if (!b) return -1;
          return a?.localeCompare(b);
        },
      },
      {
        field: "assigneeUserFullName",
        flex: 1,
        minWidth: 200,
        headerName: "Assignee",
        sortComparator: (a, b) => {
          return (
            a?.localeCompare(b) || 0
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        width: 10,
        align: "right",
        sortable: false,
        renderCell: ({ row }) => {
          const items: MoreMenuButtonProps["menuItems"] = [
            {
              label: "Edit",
              onClick: () => handleOrderEditClick(row.orderGuid),
            },
          ];

          if (["admin","owner"].includes(user?.userRole ?? "") && !row.isUnconfirmed && !["q","a"].includes(row.orderStatus) && !user?.isRetailUser) {
            items.push({
              label: "Unconfirm Order",
              onClick: () => handleUnconfirmClick(row.orderGuid),
            });
          }

          if (canAddChangeOrder(row) && !user?.isRetailUser) {
            items.push({
              label: "New Sales Change Order",
              onClick: () => handleNewChangeOrderClick(row.orderGuid),
            });
          }

          if (user && canDeleteOrder(row, user)) {
            items.push({
              label: "Delete",
              onClick: () => handleDeleteOrderClick(row.orderGuid),
            });
          }

          return <MoreMenuButton menuItems={items} />;
        },
      },
    ],
    [
      handleDeleteOrderClick,
      handleNewChangeOrderClick,
      handleOrderEditClick,
      user,
      handleUnconfirmClick,
    ]
  );

  return (
    <Container maxWidth={false}>
      <FillContainer>
        <Table
          onRowClick={(row) => handleOrderEditClick(row.row.orderGuid)}
          columns={columns}
          rows={orders}
          rowSelection={false}
          disableColumnFilter
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          getRowId={(row) => row.orderGuid}
          initialState={{
            sorting: {
              sortModel: [{ field: "orderStatus", sort: "asc" }],
            },
          }}
        />
      </FillContainer>
    </Container>
  );
};
